import { SessionStorageKeys, useSessionStorage } from '@/utils/storage.ts';
import { Language } from '@/api/types.ts';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';

export const DEFAULT_LANGUAGE = 'english';

export const languageToLocale: Record<Language, string> = {
  english: 'en',
  spanish: 'es',
  russian: 'ru',
  ukrainian: 'uk',
};

// Language state backed by a single session-storage key, kept in sync with
// i18next. Two keys exist on purpose — see the two hooks below.
const useLanguageState = (key: SessionStorageKeys) => {
  const [language, _setLanguage] = useSessionStorage<Language>(
    key,
    DEFAULT_LANGUAGE
  );
  const {
    i18n: { changeLanguage },
  } = useTranslation();

  useEffect(() => {
    changeLanguage(languageToLocale[language] ?? language);
  }, [language, changeLanguage]);

  const setLanguage = useCallback(
    (value: Language) => {
      _setLanguage(value);
      changeLanguage(languageToLocale[value] ?? value);
    },
    [_setLanguage, changeLanguage]
  );

  return [language, setLanguage, _setLanguage] as const;
};

// The apply-flow UI language. The ApplyFlowShell globe control writes this; the
// stored selection drives server content (?language=) and must never reach the
// interview — the interview language is chosen on the Welcome screen instead.
export const useAppLanguage = () =>
  useLanguageState(SessionStorageKeys.SELECTED_LANGUAGE);

// The language the interview is conducted in: the intro video and the questions.
// Written only by the Welcome screen, read only by useQuestionsAnswers.
export const useInterviewLanguage = () =>
  useLanguageState(SessionStorageKeys.INTERVIEW_LANGUAGE);
